import React, { Fragment, Suspense, useRef } from 'react';
import {
  Box,
  List,
} from '@mui/material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useInView } from '@shared/hooks/useInView';

import { useInfiniteDisciplines } from '../hooks/useInfiniteDisciplines';

interface ViewProps {
  filters?: Disciplines.GetListParams;
  renderItem: (job: Disciplines.Discipline) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
}

export const InfiniteDisciplinesList: React.FC<ViewProps> = ({ 
  filters = {}, 
  renderItem, 
  renderSkeleton, 
}) => {
  const { disciplines, status, fetchNextPage, hasNextPage, isFetching } = useInfiniteDisciplines(filters);

  const loadNextRef = useRef<HTMLElement>(undefined);
  const isInView = useInView(loadNextRef);

  const _renderSkeleton = React.useCallback(() => {
    if (isFetching) {
      return Array.from({ length: 3 }).map((v, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
    }
  }, [isFetching, renderSkeleton]);

  React.useEffect(() => {
    if (isInView && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInView, isFetching, fetchNextPage, hasNextPage]);


  return (
    <React.Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%" />
      )}
      {status === 'success' && disciplines.length === 0 ? (
        <MessageFeedbackView 
          message="No disciplines"
          height="100%"
        />
      ) : (
        <List disablePadding>
          {disciplines.map(discipline => (
            <Suspense key={discipline.id} fallback={<>{renderSkeleton()}</>}>
              {renderItem(discipline)}
            </Suspense>
          ))}
        </List>
      )}

      <Box ref={loadNextRef} />
      {hasNextPage && _renderSkeleton()}
    </React.Fragment>
  );
};
